import {
  useForm, UseFormOptions, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'

import { digObject } from '@domain-group/javascript-utils'

import defaultFormState, { CommentModel, CommentRequestOptions, requiredFields } from '@models/comment'

import useSelector from '@hooks/useSelector'

import formatDate from '@functions/formatDate'

import * as commentActions from '@redux/modules/comment'

import { AppDispatch } from '@redux/store'

type CreateCommentPayload = {
  commentParams: Partial<CommentModel>,
  dispatch: AppDispatch,
  requestOptions?: CommentRequestOptions,
}

const createComment = (params: CreateCommentPayload) => {
  const { commentParams, dispatch, requestOptions } = params
  const { createComment: createFn } = commentActions

  return dispatch(createFn(commentParams, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCommentForm(
  comment: Partial<CommentModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [] } = options

  const commentForm = useForm(
    defaultFormState,
    { entity: comment, requiredFields: [...requiredFields, ...customRequiredFields], validateOn: 'change' },
    [comment.id, comment.cache_key, comment.commentable_id],
  )

  return {
    ...commentForm,
  }
}

export const useRelations = (comment: Partial<CommentModel> = {}) => {
  const { commentable_id, commentable_type, user_id } = comment

  const { conversations, users } = useSelector(reduxState => reduxState.entities)

  // @ts-expect-error TS2538
  const conversation = commentable_type === 'Conversation' ? conversations[commentable_id] || {} : {}
  // @ts-expect-error TS2538
  const user = users[user_id] || {}

  return {
    conversation,
    user,
  }
}

function useComment(initEntity: Partial<CommentModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: comment }: { entity: CommentModel } = useLatestEntity(initEntity, 'comments')

  const dispatch = useThunkDispatch()

  const { conversation, user } = useRelations(comment)

  const createdAt = digObject(comment, 'dates.created.date_time', '')
  const formattedDate = formatDate(createdAt, 'dd/MM/yyyy HH:mm', 'MMM d, yyyy')
  const formattedTime = formatDate(createdAt, 'dd/MM/yyyy HH:mm', 'hh:mma')

  return {
    callbacks: {
      createComment: (
        params: Partial<CommentModel>,
        requestOptions?: CommentRequestOptions,
      ) => createComment({ commentParams: params, dispatch, requestOptions }),
    },
    comment,
    conversation,
    formattedDate,
    formattedTime,
    user,
  }
}

export type UseCommentPayload = ReturnType<typeof useComment>

export default useComment
